import { Delete } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import {
  deleteAttachment,
  getAttachments,
  updateAttachment,
} from "../../../../modules/entity-details";
import { attachmentTableHeaders } from "../../../project-dashboard/test-case/testCaseTableHead";
import ConfirmationModal from "../../ConfirmationModal";
import AttachmentTable from "./AttachmentTable";
import FileUpload from "./FileUpload";
import { useNotification } from "../../../../hooks/NotificationContext";
import { useParams } from "react-router-dom";
import { apiRoot } from "../../../../modules/callApi";
import { getDisplayUserName, isExecutiveManager } from "../../../../lib/userUtils";
import CustomIconButton from "../../CustomIconButton";

const Attachments = ({
  entityType,
  entityId,
  showHeader = true,
  refreshData,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const { addNotification } = useNotification();
  const token = localStorage.getItem("token");
  const { projectId } = useParams();
  const [uploading, setUploading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAttachments(entityType, entityId);
      setData(response.data);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = () => {
    fetchData();
  };

  const handleDeleteClick = (id) => {
    setEntityToDelete(id);
    setShowConfirmationModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmationModal(false);
  };

  useEffect(() => {
    if (entityId && entityType) fetchData();
  }, [entityId]);

  const handleUpdateEntity = async (id, editingValues) => {
    setLoading(true);
    try {
      const entity = { fileName: editingValues.file_name };

      await updateAttachment(id, entity);
      addNotification("success", "Updated successfully!");
      fetchData();
    } catch (error) {
      const errorMessage =
        error?.message || "Failed to update. Please try again.";
      addNotification("danger", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (entityToDelete) {
      try {
        await deleteAttachment(entityToDelete);
        fetchData();
        refreshData && refreshData();
        addNotification("success", "Deleted successfully!");
      } catch (error) {
        const errorMessage =
          error?.message || "Failed to delete. Please try again.";
        addNotification("danger", errorMessage);
      } finally {
        setShowConfirmationModal(false);
      }
    }
  };

  const handleFiles = async (files) => {
    const formData = new FormData();
    console.log("Files to upload:", files);

    files.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("entityType", entityType);
    formData.append("entityId", entityId);
    setUploading(true);
    try {
      const response = await fetch(
        `${apiRoot}attachments?projectId=${projectId}`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      if (response.ok) {
        setTimeout(() => {
          addNotification("success", "Attachment added successfully");
          handleUpload && handleUpload(response);
          setUploading(false);
          refreshData && refreshData();
        }, 500);
      } else {
        addNotification("danger", "Unable to add attachment");
      }
    } catch (error) {
      console.error("Error adding attachment:", error);
      addNotification("danger", "Unable to add attachment");
    }
    setUploading(false);
  };

  return (
    <Card className="shadow-sm mb-4">
      {showHeader && (
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ height: "50px" }}
        >
          <h5 className="mb-0">Attachments</h5>
        </Card.Header>
      )}
      <Card.Body>
        {!isExecutiveManager() && (
          <FileUpload
            enableDragAndDrop={true}
            loading={uploading}
            handleFiles={handleFiles}
          />
        )}
        <AttachmentTable
          data={
            (data &&
              data.map((att) => ({
                ...att,
                owner: getDisplayUserName(att?.user),
                file_name: att.file_name.split(".")[0],
              }))) ||
            []
          }
          headerTabs={attachmentTableHeaders}
          isLoading={loading}
          defaultSort="created_at"
          order={order}
          fetchData={fetchData}
          setOrder={setOrder}
          handleUpdateEntity={handleUpdateEntity}
          editableFields={["file_name"]}
          action={(row) => (
            <>
              <CustomIconButton
                disableRipple
                title="Delete"
                onClick={() => handleDeleteClick(row.id)}
              >
                <Delete sx={{ color: "#FF0000" }} />
              </CustomIconButton>
            </>
          )}
        />
        <ConfirmationModal
          show={showConfirmationModal}
          handleClose={handleCloseModal}
          handleConfirm={handleConfirmDelete}
        />
      </Card.Body>
    </Card>
  );
};

export default Attachments;
